export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageCropperOptions {
  maxSizeMB: number;
  minDimensions: number;
  maxDimensions: number;
  quality: number;
  targetWidth?: number;
  targetHeight?: number;
}

/**
 * Crops an image using canvas and returns a File object
 */
export async function cropImage(
  imageSrc: string,
  cropArea: CropArea,
  options: ImageCropperOptions,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Failed to get canvas context"));

          return;
        }

        // Set canvas dimensions based on target dimensions or crop area
        let canvasWidth: number;
        let canvasHeight: number;

        if (options.targetWidth && options.targetHeight) {
          // Use target dimensions if provided
          canvasWidth = options.targetWidth;
          canvasHeight = options.targetHeight;
        } else {
          // Fallback to square crop based on the crop area
          const cropSize = Math.min(cropArea.width, cropArea.height);

          canvasWidth = cropSize;
          canvasHeight = cropSize;
        }

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        // Validate minimum dimensions
        const minDimension = Math.min(canvasWidth, canvasHeight);

        if (minDimension < options.minDimensions) {
          reject(
            new Error(`Image dimensions are too small. Minimum size is ${options.minDimensions}px`),
          );

          return;
        }

        // Validate maximum dimensions
        const maxDimension = Math.max(canvasWidth, canvasHeight);

        if (maxDimension > options.maxDimensions) {
          // Scale down if needed
          const scale = options.maxDimensions / maxDimension;

          canvas.width = Math.round(canvasWidth * scale);
          canvas.height = Math.round(canvasHeight * scale);

          ctx.drawImage(
            image,
            cropArea.x,
            cropArea.y,
            cropArea.width,
            cropArea.height,
            0,
            0,
            canvas.width * scale,
            canvas.height * scale,
          );
        } else {
          // Draw the cropped image
          ctx.drawImage(
            image,
            cropArea.x,
            cropArea.y,
            cropArea.width,
            cropArea.height,
            0,
            0,
            canvas.width,
            canvas.height,
          );
        }

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to create blob from canvas"));

              return;
            }

            // Check file size
            const maxSizeBytes = options.maxSizeMB * 1024 * 1024;

            if (blob.size > maxSizeBytes) {
              reject(
                new Error(`Cropped image exceeds the maximum file size of ${options.maxSizeMB}MB`),
              );

              return;
            }

            // Create File object
            const file = new File([blob], "cropped-image.jpg", {
              type: "image/jpeg",
              lastModified: Date.now(),
            });

            resolve(file);
          },
          "image/jpeg",
          options.quality,
        );
      } catch (error) {
        reject(new Error("Failed to process image. Please try again with a different image."));
      }
    };

    image.onerror = () => {
      reject(new Error("Invalid image data. Please select a different image."));
    };

    image.src = imageSrc;
  });
}

/**
 * Reads a file and returns a data URL
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error("Failed to read file"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * Gets image dimensions from a file
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.width, height: image.height });
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    image.src = url;
  });
}
